import { EnrolmentTargetTypeEnum } from 'shared/enums/campaigns'
import { SearchableItemTypesEnum } from 'shared/enums/common'
import { t, Trans } from '@lingui/macro'
import {
  EnrolmentCreationMethodEnum,
  EventFilterTypeEnum,
  TargetEnrolmentStatusEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import React from 'react'

export enum feEnrolmentTimingsFilterEnum {
  NONE = 'NONE',
  DATE_RANGE = 'DATE_RANGE',
  LESS_THAN = 'LESS_THAN',
  MORE_THAN = 'MORE_THAN',
  BEFORE_DATE = 'BEFORE_DATE',
  AFTER_DATE = 'AFTER_DATE'
}

export const renderStatusLabel = (status: TargetEnrolmentStatusEnum) => {
  if (status === TargetEnrolmentStatusEnum.no_enrolment) {
    return <Trans>User has</Trans>
  }
  if (
    status === TargetEnrolmentStatusEnum.expires ||
    status === TargetEnrolmentStatusEnum.expired_or_expires
  ) {
    return <Trans>User enrolment</Trans>
  }
  if (status === TargetEnrolmentStatusEnum.last_viewed_at) {
    return <Trans>User enrolment was</Trans>
  }
  return <Trans>User enrolment status is</Trans>
}

export const renderStatusText = (status: TargetEnrolmentStatusEnum) => {
  const statusTranslated = {
    ...getEnrolmentStateOptionsMap(),
    ...getEnrolmentStateOptionsCertMap(),
    [TargetEnrolmentStatusEnum.expired_or_expires]: t`expires or expired`
  }[status]

  return status === TargetEnrolmentStatusEnum.any_status ? (
    <Trans>Any enrolment status</Trans>
  ) : (
    <>
      {renderStatusLabel(status)} {statusTranslated}
    </>
  )
}

export const renderCreationText = (creationMethod) => {
  return creationMethod &&
    creationMethod !== EnrolmentCreationMethodEnum.shareOrSelfAssign
    ? `, ${getEnrolmentCreatedByOptionsMap()[creationMethod]}`
    : ''
}

export const renderIncludeArchivedText = (includeArchived: boolean) => {
  return (
    includeArchived && (
      <>
        &nbsp;
        <Trans>including archived</Trans>
      </>
    )
  )
}

export const FETCH_ENTITY_MAP = {
  [EnrolmentTargetTypeEnum.certification]:
    SearchableItemTypesEnum.certifications,
  [EnrolmentTargetTypeEnum.topic]: SearchableItemTypesEnum.topics,
  [EnrolmentTargetTypeEnum.pathway]: SearchableItemTypesEnum.pathways
}

export const getTargetEntitiesTranslations = () => {
  return {
    [EnrolmentTargetTypeEnum.certification]: t`certifications`,
    [EnrolmentTargetTypeEnum.topic]: t`resources`,
    [EnrolmentTargetTypeEnum.pathway]: t`pathways`
  }
}

export const getEnrolmentStateOptionsMap = () => {
  return {
    [TargetEnrolmentStatusEnum.any_status]: t`any status`,
    [TargetEnrolmentStatusEnum.completed]: t`completed`,
    [TargetEnrolmentStatusEnum.not_completed]: t`not completed`,
    [TargetEnrolmentStatusEnum.no_enrolment]: t`no enrolment`,
    [TargetEnrolmentStatusEnum.not_completed_or_no_enrolment]: t`not completed or no enrolment`,
    [TargetEnrolmentStatusEnum.in_progress]: t`in progress`,
    [TargetEnrolmentStatusEnum.not_started]: t`not started`,
    [TargetEnrolmentStatusEnum.overdue]: t`overdue`,
    [TargetEnrolmentStatusEnum.failed]: t`failed`,
    [TargetEnrolmentStatusEnum.passed]: t`passed`,
    [TargetEnrolmentStatusEnum.last_viewed_at]: t`last viewed`
  }
}

export const getEventStatusOptionsMap = () => {
  return {
    [EventFilterTypeEnum.any_status]: t`any status`,
    [EventFilterTypeEnum.accepted]: t`accepted`,
    [EventFilterTypeEnum.declined]: t`declined`,
    [EventFilterTypeEnum.attended]: t`attended`,
    [EventFilterTypeEnum.not_attended]: t`not attended`,
    [EventFilterTypeEnum.not_responded]: t`not responded`
  }
}

export const getEnrolmentStateOptionsCertMap = () => {
  return {
    [TargetEnrolmentStatusEnum.any_status]: t`any status`,
    [TargetEnrolmentStatusEnum.completed]: t`completed`,
    [TargetEnrolmentStatusEnum.not_completed]: t`not completed`,
    [TargetEnrolmentStatusEnum.no_enrolment]: t`no enrolment`,
    [TargetEnrolmentStatusEnum.not_completed_or_no_enrolment]: t`not completed or no enrolment`,
    [TargetEnrolmentStatusEnum.in_progress]: t`in progress`,
    [TargetEnrolmentStatusEnum.not_started]: t`not started`,
    [TargetEnrolmentStatusEnum.overdue]: t`overdue`,
    [TargetEnrolmentStatusEnum.last_viewed_at]: t`last viewed`,
    [TargetEnrolmentStatusEnum.expired_or_expires]: t`expires`,
    [TargetEnrolmentStatusEnum.expires]: t`expires`
  }
}

export const getEnrolmentCreatedByOptionsMap = () => {
  return {
    [EnrolmentCreationMethodEnum.shareOrSelfAssign]: t`share or self-assign`,
    [EnrolmentCreationMethodEnum.share]: t`shared only`,
    [EnrolmentCreationMethodEnum.selfAssign]: t`self-assigned only`
  }
}

export const getEnrolmentTimingOptionsMap = (isExpiresStatus: boolean) => {
  const atAnyTimeOption = !isExpiresStatus
    ? {
        [feEnrolmentTimingsFilterEnum.NONE]: t`at any time`
      }
    : {}

  return {
    ...atAnyTimeOption,
    [feEnrolmentTimingsFilterEnum.MORE_THAN]: t`more than`,
    [feEnrolmentTimingsFilterEnum.LESS_THAN]: t`less than`,
    [feEnrolmentTimingsFilterEnum.BEFORE_DATE]: t`before date`,
    [feEnrolmentTimingsFilterEnum.AFTER_DATE]: t`after date`,
    [feEnrolmentTimingsFilterEnum.DATE_RANGE]: t`in specific date range`
  }
}

export const transformToSelectOptions = (options) => {
  return Object.entries(options).map(
    ([value, label]) =>
      ({
        value,
        label
      } as { label: string; value: string })
  )
}
